<template>
  <div class="card">
    <div style="background: #DBE7F2" class="p-2">
      <TitleButton :showBtn="false" :showAddNew="false" title="Bill of Quantity (BoQ) Status Report"/>
      <div class="row match-height mt-1">
        <div v-if="props.callFrom !== 'register'" class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" >Select Project</label>
          <v-select
              placeholder="Select Project"
              :options="projects"
              label="name"
              :reduce="name => name.id"
              v-model="projectId"
              @option:selected="onSelectProject"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel">Budget</label>
          <v-select
              placeholder="Select Budget"
              v-model="budgetMasterId"
              :options="donors"
              label="name"
              :reduce="name => name.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" >Select Cost Centre</label>
          <v-select
              placeholder="Select Cost Centre"
              v-model="costCentreId"
              :options="costCentres"
              label="name"
              :reduce="name => name.id"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel">Select Group</label>
          <v-select
              placeholder="All Groups"
              v-model="groupId"
              :options="groups"
              label="name"
              :reduce="name => name.id"
          />
        </div>
        <div class="col-12 col-lg-3 mt-2">
          <div class="d-flex">
            <button
                @click="getReportData"
                class="btn btn-primary waves-effect waves-float waves-light mx-2"
                :disabled="disableReportExportBtn"
                :class="{'disabled' : disableReportExportBtn}"
            >Go
            </button>
            <button
                @click="exportReport('donor-report-bva')"
                class="btn btn-outline-primary waves-effect waves-float waves-light"
                :disabled="disableReportExportBtn"
                :class="{'disabled' : disableReportExportBtn}"
            >Export
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="card-body" v-if="reportData && reportData.length > 0" :style="{opacity: isLoading ? .5 : 1}">
      <div class="table-responsive">
        <table class="table table-bordered table-striped table-hover">
          <thead class="table-light">
          <tr>
            <th rowspan="2" class="align-middle">Item</th>
            <th rowspan="2" class="align-middle">Linked Item</th>
            <th rowspan="2" class="align-middle text-center">Unit</th>
            <th rowspan="2" class="align-middle text-right">Quantity</th>
            <th rowspan="2" class="align-middle text-right">Rate</th>
            <th rowspan="2" class="align-middle text-right">Total Amount</th>
            <th rowspan="2" class="align-middle text-right">Modification Qty</th>
            <th rowspan="2" class="align-middle text-right">Modification Qty Amount</th>
            <th rowspan="2" class="align-middle text-right">Requisition Qty</th>
            <th rowspan="2" class="align-middle text-right">PO Qty</th>
            <th rowspan="2" class="align-middle text-right">Remaining Qty (Order)</th>
            <th rowspan="2" class="align-middle text-right">PO Qty Amount</th>
            <th rowspan="2" class="align-middle text-right">Remaining Qty Amount</th>
            <th rowspan="2" class="align-middle text-right">Stock in hand</th>
            <th rowspan="2" class="align-middle text-right">Net Qty Received</th>
            <th rowspan="2" class="align-middle text-right">Remaining BOQ Qty (Stock)</th>
          </tr>
          <tr></tr>
          </thead>
          <tbody>
          <ReportTreeBrowser
              v-for="item in reportData"
              :key="item.id"
              :node="item"
              :depth="0"
              :project-id="projectId"
              :budget-master-id="budgetMasterId"
              :cost-centre-id="costCentreId"
              :company-id="$route.params.companyId"
              :offset="offset"
          />

          <tr class="table-secondary font-weight-bold text-dark font-medium-1">
            <td colspan="3">Total:</td>
            <td class="text-right">{{ commaFormat(grandTotals.quantity) }}</td>
            <td class="text-right">{{ commaFormat(grandTotals.rate) }}</td>
            <td class="text-right">{{ commaFormat(grandTotals.budget_total) }}</td>
            <td class="text-right">0.00</td>
            <td class="text-right">0.00</td>
            <td class="text-right">{{ commaFormat(grandTotals.requisition_qty) }}</td>
            <td class="text-right">{{ commaFormat(grandTotals.po_qty) }}</td>
            <td class="text-right">{{ commaFormat(grandTotals.remaining_qty_order) }}</td>
            <td class="text-right">{{ commaFormat(grandTotals.po_amount) }}</td>
            <td class="text-right">{{ commaFormat(grandTotals.remaining_amount) }}</td>
            <td class="text-right">{{ commaFormat(grandTotals.stock_in_hand) }}</td>
            <td class="text-right">{{ commaFormat(grandTotals.net_qty_received) }}</td>
            <td class="text-right">{{ commaFormat(grandTotals.remaining_boq_qty_stock) }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="px-2 mt-2">
        <Pagination :offset="offset" @onPageChange="onPageChange" ref="pagination"/>
      </div>
    </div>

    <Loader v-if="isLoading" />
  </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton'
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import ReportTreeBrowser from "@/views/page/company/procurement/budget/report/boq-status-report/ReportTreeBrowser.vue"
import {inject, onMounted, ref} from "vue";
import {useStore} from "vuex";
import handleProjects from "@/services/modules/procurement/project";
import handleBudgets from "@/services/modules/procurement/budget";
import {useRoute, useRouter} from "vue-router";
import handleCostCentres from "@/services/modules/ngo/costCentre";
import figureFormatter from "@/services/utils/figureFormatter";

const props = defineProps({
  callFrom: {
    type: String,
    default: ''
  }
})

const {fetchProjects, fetchProjectDonors, fetchProjectGroups} = handleProjects();
const {fetchCostCentreList} = handleCostCentres()
const {fetchBoqStatusNodes} = handleBudgets();
const {commaFormat} = figureFormatter();

const $route = useRoute();
const $router = useRouter();
const $store = useStore();
const showError = inject('showError');
const query = `?company_id=${$route.params.companyId}`;
const token = localStorage.getItem('token');
const projects = ref([]);
const donors = ref([]);
const costCentres = ref([]);
const projectId = ref(null);
const budgetMasterId = ref(null);
const costCentreId = ref(null);
const isLoading = ref(false);
const disableReportExportBtn = ref(false);
const reportData = ref([]);
const groups = ref([]);
const groupId = ref(null);
const offset = 10;
const grandTotals = ref(emptyTotals());

function emptyTotals() {
  return {
    quantity: 0, rate: 0, budget_total: 0,
    requisition_qty: 0, po_qty: 0, po_amount: 0,
    remaining_amount: 0, stock_in_hand: 0, net_qty_received: 0,
    remaining_qty_order: 0, remaining_boq_qty_stock: 0,
  };
}

const onSelectProject = () => {
  isLoading.value = true;
  disableReportExportBtn.value = false;
  const selectedProject = projects.value.find(project => project.id == projectId.value);
  if (! selectedProject) {
    isLoading.value = false;
    return;
  }
  const requiredValues = [selectedProject.interval, selectedProject.start_date, selectedProject.end_date];
  if (requiredValues.includes(null) || requiredValues.includes('')) {
    disableReportExportBtn.value = true;
    showError('Project Interval, Start Date or End date is missing. Please add them to generate report');
  }
  reportData.value = [];
  grandTotals.value = emptyTotals();
  groups.value = [];
  groupId.value = null;
  resetPagination();

  Promise.all([
    fetchProjectDonors(projectId.value, query).then(res => {
      if (! res.status) return;
      budgetMasterId.value = null;
      donors.value = res.data;
    }),
    fetchProjectGroups(projectId.value, query).then(res => {
      if (! res.status) return;
      groups.value = res.data;
    })
  ])
      .catch(err => showError(err.response.message))
      .finally(() => isLoading.value = false)
}

const setPagination = (pg) => {
  if (! pg) return;
  $store.commit('setPaginationData', {
    records: pg.total,
    perPage: pg.per_page,
    currentPage: pg.current_page,
  });
}

const resetPagination = () => {
  $store.commit('setPaginationData', { records: 0, perPage: offset, currentPage: 1 });
}

const loadRoots = (page = 1) => {
  if (! projectId.value) {
    showError('Please select a project');
    return;
  }
  isLoading.value = true;

  const params = [
    `company_id=${$route.params.companyId}`,
    `budget_master_id=${budgetMasterId.value || ''}`,
    `cost_centre_id=${costCentreId.value || ''}`,
    `group_id=${groupId.value || ''}`,
    `page=${page}`,
    `offset=${offset}`,
  ].join('&');
  const budgetQuery = `?${params}`;

  fetchBoqStatusNodes(projectId.value, budgetQuery)
      .then(res => {
        if (! res.status) {
          showError(res.message);
          return;
        }
        reportData.value = res.data.data || [];
        setPagination(res.data);
        if (! res.grand_totals) return;
        grandTotals.value = res.grand_totals;
      })
      .catch(err => {
        const msg = (err.response && err.response.data && err.response.data.message) || err.message;
        showError(msg);
      })
      .finally(() => isLoading.value = false);
}

const getReportData = () => {
  const routeQuery = Object.assign({}, $route.query, { page: 1 });
  $router.push({ path: $route.path, query: routeQuery });
  loadRoots(1);
}

const onPageChange = (page) => {
  const routeQuery = Object.assign({}, $route.query, { page });
  $router.push({ path: $route.path, query: routeQuery });
  loadRoots(page);
}

const exportReport = (type = 'project-lifecycle-boq') => {
  if (! projectId.value) {
    showError('Please select a project');
    return;
  }
  const objQuery = {
    company_id: $route.params.companyId,
    budget_master_id: budgetMasterId.value || '',
    cost_centre_id: costCentreId.value || '',
    group_id: groupId.value || '',
    _token: token,
    report_type: "boq-status-report",
    report_title: "Bill of Quantity (BoQ) Status Report"
  }
  const params = Object.entries(objQuery).map(([key, val]) => `${key}=${val}`).join('&');
  const url = `${process.env.VUE_APP_BASE_URL}/export/${type}/${projectId.value}?${params}`;
  const a = document.createElement('a');
  a.setAttribute('href', url);
  a.click();
}

onMounted(() => {
  isLoading.value = true;
  const projectQuery = `${query}&time_and_interval=1`;
  Promise.all([
    fetchProjects(projectQuery).then(res => {
      if (! res.status) return;
      projects.value = res.data;
    }),
    fetchCostCentreList(query).then(res => {
      if (! res.status) return;
      costCentres.value = res.data;
    })
  ])
      .then(() => {
        if (! $route.query.projectId) return;
        projectId.value = $route.query.projectId;
        onSelectProject();
      })
      .catch(err => showError(err.response.message))
      .finally(() => isLoading.value = false);
})
</script>

<style scoped>
.text-right { text-align: right; }
.text-center { text-align: center; }
.align-middle { vertical-align: middle; }
.d-flex.gap-1 { gap: 0.25rem; }
.font-weight-bold { font-weight: 600; }
.font-medium-1 { font-size: 1.1rem; }
</style>
